import axios from 'axios';
import { getAuthHeaders } from './api';
import { toast } from "../components/ThreatModelling/ui/use-toast";
import { error, log } from '../utils/logger';
import { User } from '../types/auth';
import { getUserData } from 'utils/encryption';
import { joinUrl } from 'lib/utils';

const ADMIN_DASHBOARD_URL = `${process.env.REACT_APP_OAUTH_URL || 'https://monitor.openana.ai'}api/v1`;

const API_KEY = process.env.REACT_APP_SECURITY_API_KEY || 'ana_security_prod_2025_w7y5z3a1b9c4d6f8';

/**
 * Log trial activity to the admin dashboard
 * This function logs trial activity from the security app to the admin dashboard
 */
export const logTrialActivity = async (payload: {
  user_email: string;
  activity_type: string; // "scan" | "fix" | "analysis" | "status_check"
  trial_source?: string; // "jwt" | "database"
  trial_status?: string; // "active" | "expired" | "unlimited" | "inactive"
  credits_used?: number;
  remaining_credits?: number;
  scan_count?: number;
  fix_count?: number;
  trial_expires?: string;
  days_remaining?: number;
  request_details?: any;
  response_details?: any;
  ip_address?: string;
  user_agent?: string;
}): Promise<TrialActivity | null> => {
  try {
    // Set default values for optional fields
    const defaultedPayload = {
      trial_source: "jwt",
      trial_status: "active",
      ...payload,
      // Ensure scan_count and fix_count are set based on activity_type
      scan_count: payload.activity_type === "scan" ? (payload.scan_count || 1) : 0,
      fix_count: payload.activity_type === "fix" ? (payload.fix_count || 1) : 0,
      // Set user agent if not provided
      user_agent: payload.user_agent || (typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown')
    };

    // Make API call to log trial activity
    const response = await axios.post<TrialActivity>(
      joinUrl(ADMIN_DASHBOARD_URL, `/trials/activity?api_key=${API_KEY}`),
      defaultedPayload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    log('Trial activity logged successfully:', response.data);
    return response.data;
  } catch (err: any) {
    error('Error logging trial activity:', err);
    return null;
  }
};

/**
 * Log scan activity (convenience method)
 */
export const logScanActivity = async (
  userEmail: string,
  creditsUsed: number = 2.0,
  remainingCredits?: number,
  scanDetails?: {
    filesCount?: number;
    vulnerabilities?: number;
    duration?: number;
    endpoint?: string;
  }
): Promise<TrialActivity | null> => {
  try {
    const user = getUserData();

    return await logTrialActivity({
      user_email: userEmail,
      activity_type: "scan",
      trial_source: "jwt",
      trial_status: user?.security_trial ? "active" : "inactive",
      credits_used: creditsUsed,
      remaining_credits: remainingCredits || user?.remaining_credits,
      scan_count: 1,
      fix_count: 0,
      trial_expires: user?.security_trial_expires,
      days_remaining: user?.security_trial_expires ?
        Math.max(0, Math.ceil((new Date(user.security_trial_expires).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) :
        undefined,
      request_details: {
        endpoint: scanDetails?.endpoint || "/security/scan",
        files_scanned: scanDetails?.filesCount || 0,
        vulnerabilities_found: scanDetails?.vulnerabilities || 0
      },
      response_details: {
        status: "completed",
        duration_ms: scanDetails?.duration || 0,
        success: true
      }
    });
  } catch (err: any) {
    error('Error logging scan activity:', err);
    return null;
  }
};

/**
 * Log fix activity (convenience method)
 */
export const logFixActivity = async (
  userEmail: string,
  creditsUsed: number = 3.0,
  remainingCredits?: number,
  fixDetails?: {
    vulnerabilitiesFixed?: number;
    filesModified?: number;
    duration?: number;
    endpoint?: string;
  }
): Promise<TrialActivity | null> => {
  try {
    const user = getUserData();

    return await logTrialActivity({
      user_email: userEmail,
      activity_type: "fix",
      trial_source: "jwt",
      trial_status: user?.security_trial ? "active" : "inactive",
      credits_used: creditsUsed,
      remaining_credits: remainingCredits || user?.remaining_credits,
      scan_count: 0,
      fix_count: 1,
      trial_expires: user?.security_trial_expires,
      days_remaining: user?.security_trial_expires ?
        Math.max(0, Math.ceil((new Date(user.security_trial_expires).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) :
        undefined,
      request_details: {
        endpoint: fixDetails?.endpoint || "/security/fix",
        vulnerabilities_fixed: fixDetails?.vulnerabilitiesFixed || 0,
        files_modified: fixDetails?.filesModified || 0
      },
      response_details: {
        status: "completed",
        duration_ms: fixDetails?.duration || 0,
        success: true
      }
    });
  } catch (err: any) {
    error('Error logging fix activity:', err);
    return null;
  }
};

// Trial Activity Data Models
export interface TrialActivity {
  id: number;
  user_id: number;
  organization_id?: number;
  app_type: string; // "security" | "ide" | "web" | "devops"
  activity_type: string; // "scan" | "fix" | "analysis" | "status_check"
  trial_source: string; // "jwt" | "database"
  trial_status: string; // "active" | "expired" | "unlimited" | "inactive"
  credits_used?: number;
  remaining_credits?: number;
  scan_count?: number;
  fix_count?: number;
  trial_expires?: string; // ISO 8601 datetime
  days_remaining?: number;
  request_details?: any; // JSON object
  response_details?: any; // JSON object
  ip_address?: string;
  user_agent?: string;
  created_at: string; // ISO 8601 datetime
}

// Enhanced TrialActivity with user details for recent activities endpoint
export interface EnhancedTrialActivity extends TrialActivity {
  user_email: string;
  user_name: string;
}

export interface TrialUserSummary {
  user_id: number;
  user_email: string;
  user_name: string;
  organization_name?: string;
  total_activities: number;
  total_scans: number;
  total_fixes: number;
  total_credits_used: number;
  current_trial_status: string;
  trial_expires?: string;
  days_remaining?: number;
  last_activity?: string;
  apps_used: string[];
}

export interface TrialAnalytics {
  total_activities: number;
  unique_users: number;
  active_trials: number;
  expired_trials: number;
  total_scans: number;
  total_fixes: number;
  total_credits_used: number;
  by_app_type: Record<string, {
    total_activities: number;
    total_scans: number;
    total_fixes: number;
    total_credits_used: number;
    unique_users: number;
  }>;
  by_trial_status: Record<string, number>;
  recent_activities: TrialActivity[];
}

export interface TrialActivitiesResponse {
  data: TrialActivity[];
  meta: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export interface TrialStatsResponse {
  data: Array<{
    id: number;
    user_id: number;
    organization_id?: number;
    app_type: string;
    date: string;
    total_scans: number;
    total_fixes: number;
    total_credits_used: number;
    unique_sessions: number;
    trial_status_at_end: string;
    created_at: string;
    updated_at: string;
  }>;
  meta: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

export interface RecentActivitiesResponse {
  data: EnhancedTrialActivity[];
  meta: {
    total: number;
    limit: number;
  };
}

export interface UsageSummaryResponse {
  period: {
    days: number;
    start_date: string;
    end_date: string;
  };
  totals: {
    activities: number;
    scans: number;
    fixes: number;
    credits_used: number;
    unique_users: number;
  };
  by_app: Record<string, {
    activities: number;
    scans: number;
    fixes: number;
    credits_used: number;
  }>;
  daily_breakdown: Array<{
    date: string;
    activities: number;
    scans: number;
    fixes: number;
    credits_used: number;
  }>;
}

export interface TrialCheckResponse {
  valid: boolean;
  status: string;
  message: string;
  details: {
    user_id: number;
    role: string;
    security_trial: boolean;
    security_trial_expires?: string;
    use_credit_system: boolean;
    remaining_credits: number;
    days_remaining?: number;
  };
}

export interface TrialIntegrateResponse {
  success: boolean;
  headers: Record<string, string>;
  message: string;
}

/**
 * Check trial validity (mimics updated frontend logic)
 * This function checks if a user's trial is valid based on various conditions
 */
export const checkTrialValidity = (user: User | null): boolean => {
  if (!user) return false;

  // Super admin always has access
  if (user.role === 'super_admin') return true;

  // For development/testing: if user has credits, allow access even without trial
  // This handles cases where trial flags are false but user should have access
  if (user.use_credit_system && (user.remaining_credits || 0) > 0) {
    return true;
  }

  // Check if security trial is active
  if (!user.security_trial) return false;

  // Check if trial has expired
  if (user.security_trial_expires) {
    const expiryDate = new Date(user.security_trial_expires);
    const now = new Date();
    if (expiryDate <= now) return false;
  }

  return true;
};

/**
 * Create trial headers for API requests (integration function)
 * This function creates headers for trial-related API requests
 */
export const createTrialHeaders = (user: User | null): Record<string, string> => {
  if (!user) return {};

  return {
    'x-security-trial': (user.security_trial || false).toString(),
    'x-security-trial-expires': user.security_trial_expires || '',
    'x-use-credit-system': (user.use_credit_system || false).toString(),
    'x-remaining-credits': (user.remaining_credits || 0).toString(),
    'x-user-role': user.role || 'user'
  };
};

/**
 * Create complete headers for API requests (integration function)
 * This function combines trial headers with other necessary headers
 */
export const createApiHeaders = (user: User | null, jwtToken: string): Record<string, string> => {
  const trialHeaders = createTrialHeaders(user);

  return {
    'Authorization': `Bearer ${jwtToken}`,
    'x-user-id': user?.user_id?.toString() || '',
    'x-org-id': user?.org_names_list?.[0] || user?.email || '',
    'Content-Type': 'application/json',
    ...trialHeaders
  };
};

/**
 * API call to check trial status
 * This makes an actual API call to check the trial status
 */
export const checkTrialStatus = async (user: User | null): Promise<TrialCheckResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    // Add trial headers to the request
    const trialHeaders = createTrialHeaders(user);
    const combinedHeaders = { ...headers, ...trialHeaders };

    const response = await axios.get<TrialCheckResponse>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/check'),
      {
        headers: combinedHeaders,
        params: {
          app_type: 'security'
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error checking trial status:', err);

    // If API fails, fall back to local check
    const isValid = checkTrialValidity(user);

    // Return a fallback response
    return {
      valid: isValid,
      status: isValid ? 'active' : 'inactive',
      message: isValid ? 'Trial is valid' : 'Trial is not valid',
      details: {
        user_id: user?.user_id || 0,
        role: user?.role || 'user',
        security_trial: user?.security_trial || false,
        security_trial_expires: user?.security_trial_expires,
        use_credit_system: user?.use_credit_system || false,
        remaining_credits: user?.remaining_credits || 0
      }
    };
  }
};

/**
 * API call to integrate trial headers
 * This makes an actual API call to integrate trial headers
 */
// export const integrateTrialHeaders = async (user: User | null): Promise<TrialIntegrateResponse> => {
//   try {
//     const headers = getAuthHeaders();
//     if (!headers) {
//       throw new Error('No authentication token found');
//     }

//     // Create trial headers
//     const trialHeaders = createTrialHeaders(user);

//     // Make API call to integrate trial headers
//     const response = await axios.post<TrialIntegrateResponse>(
//       `${ADMIN_DASHBOARD_URL}/trials/integrate`,
//       { user_id: user?.user_id },
//       { headers: { ...headers, ...trialHeaders } }
//     );

//     return response.data;
//   } catch (err: any) {
//     error('Error integrating trial headers:', err);

//     // If API fails, return a fallback response with the headers we would have sent
//     return {
//       success: false,
//       headers: createTrialHeaders(user),
//       message: 'Failed to integrate trial headers with API, using local headers'
//     };
//   }
// };

/**
 * Get comprehensive trial summary for a specific user
 */
export const getUserTrialSummary = async (userId: number): Promise<TrialUserSummary> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<TrialUserSummary>(
      joinUrl(ADMIN_DASHBOARD_URL, `trials/users/${userId}/summary`),
      {
        headers,
        params: {
          app_type: 'security'
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching user trial summary:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch trial summary.'
    });
    throw err;
  }
};

/**
 * List trial activities with filtering
 */
export const getTrialActivities = async (params: {
  skip?: number;
  limit?: number;
  app_type?: string;
  activity_type?: string;
  trial_status?: string;
  user_id?: number;
  organization_id?: number;
  start_date?: string;
  end_date?: string;
} = {}): Promise<TrialActivitiesResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<TrialActivitiesResponse>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/activities'),
      {
        headers,
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          ...params
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching trial activities:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch trial activities.'
    });
    throw err;
  }
};

/**
 * Get comprehensive trial usage analytics (Super Admin Only)
 */
export const getTrialAnalytics = async (params: {
  days?: number;
  app_type?: string;
  organization_id?: number;
} = {}): Promise<TrialAnalytics> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<TrialAnalytics>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/analytics'),
      {
        headers,
        params: {
          days: params.days || 30,
          app_type: 'security',
          ...params
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching trial analytics:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch trial analytics.'
    });
    throw err;
  }
};

/**
 * Get daily usage statistics
 */
export const getTrialStats = async (params: {
  skip?: number;
  limit?: number;
  app_type?: string;
  user_id?: number;
  organization_id?: number;
  start_date?: string;
  end_date?: string;
} = {}): Promise<TrialStatsResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<TrialStatsResponse>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/stats'),
      {
        headers,
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          app_type: 'security',
          ...params
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching trial stats:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch trial stats.'
    });
    throw err;
  }
};

/**
 * Get recent activities with user details
 */
export const getRecentActivities = async (params: {
  limit?: number;
  user_id?: number;
  app_type?: string;
  activity_type?: string;
} = {}): Promise<RecentActivitiesResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<RecentActivitiesResponse>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/recent'),
      {
        headers,
        params: {
          limit: params.limit || 10,
          ...params
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching recent activities:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch recent activities.'
    });
    throw err;
  }
};

/**
 * Get usage summary for dashboard widgets
 */
export const getUsageSummary = async (params: {
  user_id?: number;
  organization_id?: number;
  days?: number;
} = {}): Promise<UsageSummaryResponse> => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error('No authentication token found');
    }

    const response = await axios.get<UsageSummaryResponse>(
      joinUrl(ADMIN_DASHBOARD_URL, 'trials/usage-summary'),
      {
        headers,
        params: {
          days: params.days || 7,
          app_type: 'security',
          ...params
        }
      }
    );

    return response.data;
  } catch (err: any) {
    error('Error fetching usage summary:', err);
    toast({
      variant: "destructive",
      title: "Error",
      description: err.response?.data?.detail || 'Failed to fetch usage summary.'
    });
    throw err;
  }
};

/**
 * Get current user's trial summary (convenience method)
 */
export const getCurrentUserTrialSummary = async (): Promise<TrialUserSummary | null> => {
  try {
    // Get user data from localStorage to get user_id
    const user = getUserData();
    if (!user) {
      log('No user data found in localStorage');
      return null;
    }

    if (!user?.user_id) {
      log('No user_id found in user data');
      return null;
    }

    return await getUserTrialSummary(user.user_id);
  } catch (err: any) {
    error('Error fetching current user trial summary:', err);
    return null;
  }
};

/**
 * Get current user's recent trial activities (convenience method)
 */
export const getCurrentUserTrialActivities = async (limit: number = 10): Promise<TrialActivity[]> => {
  try {
    // Get user data from localStorage to get user_id
    const user = getUserData()
    if (!user) {
      return [];
    }

    if (!user?.user_id) {
      return [];
    }

    const response = await getTrialActivities({
      user_id: user.user_id,
      limit,
      app_type: 'security' // Filter to security app activities
    });

    return response.data;
  } catch (err: any) {
    error('Error fetching current user trial activities:', err);
    return [];
  }
};

/**
 * Check and get current user's trial status (convenience method)
 */
export const checkCurrentUserTrialStatus = async (): Promise<TrialCheckResponse> => {
  try {
    // Get user data from localStorage
    const user = getUserData();
    if (!user) {
      throw new Error('No user data found in localStorage');
    }

    return await checkTrialStatus(user);
  } catch (err: any) {
    error('Error checking current user trial status:', err);

    // Return a fallback response
    return {
      valid: false,
      status: 'error',
      message: 'Failed to check trial status',
      details: {
        user_id: 0,
        role: 'user',
        security_trial: false,
        use_credit_system: false,
        remaining_credits: 0
      }
    };
  }
};

/**
 * Integrate current user's trial headers (convenience method)
 */
// export const integrateCurrentUserTrialHeaders = async (): Promise<TrialIntegrateResponse> => {
//   try {
//     // Get user data from localStorage
//     const user = getUserData();
//     if (!user) {
//       throw new Error('No user data found in localStorage');
//     }

//     return await integrateTrialHeaders(user);
//   } catch (err: any) {
//     error('Error integrating current user trial headers:', err);

//     // Return a fallback response
//     return {
//       success: false,
//       headers: {},
//       message: 'Failed to integrate trial headers'
//     };
//   }
// };

/**
 * Get current user's recent activities with user details (convenience method)
 */
export const getCurrentUserRecentActivities = async (limit: number = 10): Promise<EnhancedTrialActivity[]> => {
  try {
    // Get user data from localStorage to get user_id
    const user = getUserData();
    if (!user || !user.user_id) {
      return [];
    }

    const response = await getRecentActivities({
      user_id: user.user_id,
      limit,
      app_type: 'security' // Filter to security app activities
    });

    return response.data;
  } catch (err: any) {
    error('Error fetching current user recent activities:', err);
    return [];
  }
};

/**
 * Get current user's usage summary (convenience method)
 */
export const getCurrentUserUsageSummary = async (days: number = 7): Promise<UsageSummaryResponse | null> => {
  try {
    // Get user data from localStorage to get user_id
    const user = getUserData();
    if (!user || !user.user_id) {
      return null;
    }

    return await getUsageSummary({
      user_id: user.user_id,
      days
    });
  } catch (err: any) {
    error('Error fetching current user usage summary:', err);
    return null;
  }
};